import React, { useState } from 'react';
import { Avatar, Divider, IconButton, Menu, MenuItem, Box, Typography } from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import { Settings } from "@mui/icons-material";
import ListItemIcon from '@mui/material/ListItemIcon';
import LogoutIcon from '@mui/icons-material/Logout';
import { useProfileImage } from '@/api/profile';
import { useLogout, useSetCurrentBusinessUnit } from '@/hooks/api/useAuth';
import { useUpdateMe } from '@/hooks/api/useUsers';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const UserMenu: React.FC = () => {

  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: profileImageResponse } = useProfileImage({ enabled: !!user?.id });
  const logoutMutation = useLogout();
  const setBuMutation = useSetCurrentBusinessUnit();
  const updateMeMutation = useUpdateMe();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  // Get user initials for avatar fallback
  const initials = user?.initials || 
    ((user?.firstName?.[0] || '') + (user?.lastName?.[0] || '')) || 
    (user?.email?.[0]?.toUpperCase() || 'U');

  const profileImageUrl =
    profileImageResponse?.profileImageUrl ?? user?.profileImageUrl ?? undefined;

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (lang: string) => {
    setAnchorEl(null);
    updateMeMutation.mutate({
      language: lang
    });
  }

  const businessUnits = (user as any)?.businessUnits as
    | { id: number; name: string }[]
    | undefined;
  const currentBuId =
    (user as any)?.currentBusinessUnitId ??
    (user as any)?.currentBusinessUnit?.id;

  return (
    <div>
      <IconButton
        aria-label="user menu"
        aria-controls="user-menu"
        aria-haspopup="true"
        onClick={handleClick}
        sx={{ p: 0 }}
      >
        {user?.email ? (
          <Avatar
            src={profileImageUrl || undefined}
            sx={{ width: 32, height: 32 }}
          >
            {!profileImageUrl && initials}
          </Avatar>
        ) : (
          <LanguageIcon />
        )}
      </IconButton>
      <Menu
        id="user-menu"
        anchorEl={anchorEl}
        keepMounted
        open={open}
        onClose={handleClose}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            maxWidth: 280,
            pointerEvents: 'none',
          }}
        >
          <Avatar src={profileImageUrl || undefined} sx={{ width: 40, height: 40 }}>
            {!profileImageUrl && initials}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {[user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'Account'}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {user?.email}
            </Typography>
          </Box>
        </Box>
        <Divider />
        <MenuItem onClick={() => {
          handleClose();
          navigate('/app/settings');
        }}>
          <ListItemIcon>
            <Settings fontSize="small" />
          </ListItemIcon>
          Settings
        </MenuItem>
        {(businessUnits?.length ?? 0) > 1 ? (
          <>
            <Divider />
            <MenuItem disabled sx={{ opacity: 1, fontSize: 12, fontWeight: 600 }}>
              Business unit
            </MenuItem>
            {businessUnits!.map((bu) => (
              <MenuItem
                key={bu.id}
                selected={bu.id === currentBuId}
                onClick={() => {
                  handleClose();
                  setBuMutation.mutate(bu.id);
                }}
              >
                {bu.name}
              </MenuItem>
            ))}
          </>
        ) : null}
        <Divider />
        <MenuItem onClick={() => { handleSelect('en') }}>
          English
        </MenuItem>
        <MenuItem onClick={() => { handleSelect('es') }}>
          Spanish
        </MenuItem>
        <MenuItem onClick={() => { handleSelect('vi') }}>
          Vietnamese
        </MenuItem>
        <MenuItem onClick={() => { handleSelect('de') }}>
          German
        </MenuItem>
        <MenuItem onClick={() => { handleSelect('it') }}>
          Italian
        </MenuItem>
        <MenuItem onClick={() => { handleSelect('pt') }}>
          Portuguese
        </MenuItem>
        <MenuItem onClick={() => { handleSelect('ja') }}>
          Japanese
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => {
          logoutMutation.mutate();
        }}>
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>


    </div>
  );
};

export default UserMenu;